<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    show: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const isVisible = ref(false);
const isAnimating = ref(false);

watch(() => props.show, (val) => {
    if (val) {
        isVisible.value = true;
        requestAnimationFrame(() => { isAnimating.value = true; });
    } else {
        isAnimating.value = false;
        setTimeout(() => { isVisible.value = false; }, 300);
    }
});

function close() {
    emit('close');
}

function onBackdropClick(e) {
    if (e.target === e.currentTarget) close();
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isVisible"
            class="filter-drawer-backdrop"
            :class="{ 'is-open': isAnimating }"
            @click="onBackdropClick"
        >
            <div class="filter-drawer" :class="{ 'is-open': isAnimating }">
                <div class="filter-drawer-header">
                    <h3 class="filter-drawer-title">Filtre</h3>
                    <button class="filter-drawer-close" @click="close" aria-label="Luk filtre">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
                <div class="filter-drawer-body">
                    <slot />
                </div>
                <div class="filter-drawer-footer">
                    <button class="btn-pc-solid" style="width: 100%;" @click="close">
                        Vis resultater
                    </button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
.filter-drawer-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: rgba(0, 0, 0, 0);
    transition: background var(--pc-transition-slow);
}

.filter-drawer-backdrop.is-open {
    background: rgba(0, 0, 0, 0.5);
}

.filter-drawer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 85vh;
    background: var(--pc-bg-secondary);
    border-top: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-lg) var(--pc-radius-lg) 0 0;
    box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.3);
    transform: translateY(100%);
    transition: transform var(--pc-transition-slow);
    display: flex;
    flex-direction: column;
    z-index: 1051;
}

.filter-drawer.is-open {
    transform: translateY(0);
}

.filter-drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--pc-border);
    flex-shrink: 0;
}

.filter-drawer-title {
    font-size: var(--pc-text-lg);
    font-weight: 600;
    color: var(--pc-text-primary);
    margin: 0;
}

.filter-drawer-close {
    background: none;
    border: none;
    color: var(--pc-text-muted);
    font-size: 20px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: var(--pc-radius-sm);
    transition: all var(--pc-transition);
}

.filter-drawer-close:hover {
    color: var(--pc-text-primary);
    background: var(--pc-bg-hover);
}

.filter-drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
    -webkit-overflow-scrolling: touch;
}

.filter-drawer-footer {
    padding: 16px 20px;
    border-top: 1px solid var(--pc-border);
    flex-shrink: 0;
}

/* Handle area for touch drag */
.filter-drawer::before {
    content: '';
    display: block;
    width: 40px;
    height: 4px;
    background: var(--pc-border-hover);
    border-radius: 2px;
    margin: 8px auto 0;
}
</style>
